import { useEffect, useRef } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { ApiError, apiFetch } from "@/lib/api";

// ─── pipeline ──────────────────────────────────────────────────────

export type FirstTermStartSource =
  | "first_billed"
  | "signed"
  | "custom"
  | "ai_suggested";

export interface PipelineAnnotation {
  qb_id: number;
  csm_user_id: string | null;
  csm_display_name: string | null;
  csm_suggestion: {
    user_id: string | null;
    display_name: string;
    source: string;
  } | null;
  /** Partner-as-CSM override: a partner account assigned to manage this
   *  customer's Cinch services. Mutually exclusive with csm_user_id. */
  csm_partner_qb_id: number | null;
  csm_partner_name: string | null;
  renewal_anchor: string;
  renewal_date_override: string | null;
  /** First contractual term start, always first-of-month. Drives the
   *  walk-forward renewal-month derivation in pipeline_service when set. */
  first_term_start_date: string | null;
  first_term_start_source: FirstTermStartSource | null;
  forecast: string;
  renewal_status: string;
  notes: string | null;
  updated_at: string | null;
}

export interface PipelineAccountLink {
  id: string;
  source_qb_id: number;
  target_qb_id: number;
  relationship: "merged_into" | "split_from" | "subcustomer_of" | "continuation_of";
  effective_period: string | null;
  note: string | null;
  created_by_name: string | null;
  created_at: string;
  other_qb_id: number;
  other_name: string | null;
}

/** Per-cycle session block exposed inline on the pipeline row (and via
 *  `GET /accounts/{qb_id}/sessions` for the full history). The account's
 *  OPEN cycle — null when the account has no first_term_start_date set
 *  (or, transiently, when the session hasn't materialized yet; the backend
 *  creates it on anchor set and resolves it on first write). The FE gates
 *  the inline forecast/notes editors on the anchor, falling back to the
 *  annotation mirror for display until the session exists. */
export interface RenewalCurrentSession {
  id: string;
  renewal_period: string;
  forecast: string;
  renewal_status: string;
  notes: string | null;
  csm_user_id: string | null;
  outcome: "renewed" | "churned" | "expansion" | "contraction" | null;
  outcome_arr_delta: number | null;
  closed_at: string | null;
}

export interface PipelineRow {
  account_name: string;
  /** Store/DBA names on the account, folded into one ` · `-delimited string
   *  (from cinch_app_compatibility.companies, active or not). Included in the
   *  pipeline search haystack so an account is findable by the location name a
   *  CSM knows — e.g. "Kwik Kar Fort Worth" — even when its legal account name
   *  differs or is misspelled. Null when no companies are linked. */
  company_names: string | null;
  qb_id: number;
  industry_name: string | null;
  closer_name: string | null;
  closer_team: string | null;
  contract_type_name: string | null;
  is_reseller: boolean;
  contract_signed_at: string | null;
  first_billed_month: string | null;
  last_billed_month: string | null;
  months_since_billing: number | null;
  current_mrr: number;
  last_billed_mrr: number;
  current_arr: number;
  last_billed_arr: number;
  current_tier: number | null;
  renewal_from_billing: string | null;
  renewal_from_contract: string | null;
  renewal_month: string | null;
  last_renewal_month: string | null;
  months_since_last_renewal: number | null;
  active_renewal_month: string | null;
  active_months_to_renewal: number | null;
  months_to_renewal: number | null;
  billing_status: string;
  impl_lag_months: number | null;
  is_merged: boolean;
  account_links: {
    outgoing: PipelineAccountLink[];
    incoming: PipelineAccountLink[];
  };
  annotation: PipelineAnnotation;
  /** P7: per-cycle session — forecast/notes/status now live here, not on
   *  the account-level annotation. Null when the account isn't anchored. */
  current_session: RenewalCurrentSession | null;
  /** The cycle that renewed at `last_renewal_month` (the just-passed
   *  anniversary). Carries the forecast/notes the CSM recorded for that cycle
   *  plus its rollover-inferred outcome. Drives the read-only Forecast/Notes
   *  cells in the "Past" pipeline view, where `current_session` is already the
   *  next (fresh) cycle. Null when no session exists at that exact period. */
  past_session: RenewalSessionRow | null;
}

export interface PipelineResponse {
  rows: PipelineRow[];
  last_synced_at: string | null;
  anchor: "billing" | "contract";
}

export function usePipeline(anchor: "billing" | "contract" = "billing") {
  return useQuery({
    queryKey: ["renewals", "pipeline", anchor],
    queryFn: () => apiFetch<PipelineResponse>(`/renewals/pipeline?anchor=${anchor}`),
  });
}

/** Count of accounts that still need a first_term_start_date set.
 *  Drives the conditional SubNav "Setup" entry — the tab + badge appear
 *  until every non-merged account is anchored. */
export function usePipelineSetupCount() {
  return useQuery({
    queryKey: ["renewals", "pipeline", "setup-count"],
    queryFn: () => apiFetch<{ pending: number }>("/renewals/pipeline/setup-count"),
    staleTime: 60_000,
  });
}

// ─── CSM directory (HubSpot owner roster) ──────────────────────────

export interface CsmDirectoryEntry {
  hubspot_owner_id: string;
  display_name: string;
  email: string | null;
  is_cs_team: boolean;
  synced_at: string | null;
  /** Set once this owner's email matches a cinch_ops user — i.e. they've
   *  signed in and can be hard-assigned as a CSM. */
  user_id: string | null;
  has_login: boolean;
  last_login: string | null;
  assigned_count: number;
}

export interface CsmDirectoryResponse {
  entries: CsmDirectoryEntry[];
  synced_at: string | null;
}

/** The HubSpot CSM directory — who's assignable and who has signed in.
 *  Backs the renewals "CSMs" tab. */
export function useCsmDirectory() {
  return useQuery({
    queryKey: ["renewals", "csms"],
    queryFn: () => apiFetch<CsmDirectoryResponse>("/renewals/csms"),
    staleTime: 60_000,
  });
}

export function useForceSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<unknown>("/renewals/sync", { method: "POST" }),
    // Fire-and-forget: returning the invalidate promise would keep the
    // mutation `isPending` until every refetch settles. See objectives/api.ts.
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["renewals"] }),
  });
}

export interface AnnotationUpdate {
  forecast?: string;
  renewal_status?: string;
  notes?: string | null;
  csm_user_id?: string | null;
  /** Assign a partner account as the managing CSM. Sending a value clears
   *  csm_user_id; send null to clear the partner assignment. */
  csm_partner_qb_id?: number | null;
  renewal_anchor?: string;
  renewal_date_override?: string | null;
  /** First-of-month date; server normalizes if a mid-month date is sent. */
  first_term_start_date?: string | null;
  first_term_start_source?: FirstTermStartSource | null;
}

/** Every renewal cycle (past + current) for an account, newest first.
 *  Drives the "Renewal cycles" section in the expanded drawer. */
export interface RenewalSessionRow {
  id: string;
  qb_id: number;
  renewal_period: string;
  forecast: string;
  renewal_status: string;
  notes: string | null;
  csm_user_id: string | null;
  outcome: "renewed" | "churned" | "expansion" | "contraction" | null;
  outcome_arr_delta: number | null;
  closed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export function useAccountSessions(qbId: number) {
  return useQuery({
    queryKey: ["renewals", "account", qbId, "sessions"],
    queryFn: () =>
      apiFetch<{ sessions: RenewalSessionRow[] }>(
        `/renewals/accounts/${qbId}/sessions`,
      ),
    enabled: !!qbId,
  });
}

/** Response from POST /annotations/{qbId}/suggest-term-start.
 *  Deterministic Python picks the date; Ollama sanity-checks and may flag it.
 *  `ai_error` is set when the LLM was unreachable — the suggested_date is
 *  still authoritative, the AI assessment just degraded to "ok". */
export interface TermStartSuggestion {
  suggested_date: string | null;
  source: "ai_suggested" | null;
  deterministic_reason: string;
  ai_assessment: "ok" | "warning" | null;
  ai_note: string | null;
  ai_error: string | null;
  ai_model: string | null;
}

export function useSuggestTermStart() {
  return useMutation({
    mutationFn: (qbId: number) =>
      apiFetch<TermStartSuggestion>(
        `/renewals/annotations/${qbId}/suggest-term-start`,
        { method: "POST" },
      ),
  });
}

export function useUpsertAnnotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ qbId, payload }: { qbId: number; payload: AnnotationUpdate }) =>
      apiFetch<PipelineAnnotation>(`/renewals/annotations/${qbId}`, {
        method: "PUT",
        body: payload,
      }),
    // The drawer reads from useAccountDetail (per-qb), not from the pipeline
    // list directly, so we have to invalidate both queries to keep the
    // open-drawer state in sync with the edit.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["renewals", "pipeline"] });
      void qc.invalidateQueries({ queryKey: ["renewals", "account"] });
    },
    onError: (err) => {
      // The P7 guardrail returns 409 { code: "no_renewal_date", message } when
      // a session-scoped field (forecast / renewal_status / notes) is written
      // without a first_term_start_date set. Surface it as a friendly toast
      // rather than letting the FE silently rollback to the prior value.
      if (err instanceof ApiError && err.status === 409) {
        const payload = err.payload as { code?: string; message?: string } | undefined;
        if (payload?.code === "no_renewal_date") {
          toast.warning(payload.message ?? "Set a renewal date before recording a forecast.", {
            description: "Use the Renewal anchor section above to pick a first-term start date.",
          });
          return;
        }
      }
      toast.error(err instanceof Error ? err.message : "Could not save.");
    },
  });
}

// ─── dashboard ────────────────────────────────────────────────────

export interface NrrPoint {
  month: string;
  starting_arr: number;
  ending_arr: number;
  expansion: number;
  contraction: number;
  churn: number;
  nrr_pct: number | null;
  gdr_pct: number | null;
  cohort_count: number | null;
  surviving_count: number | null;
}

export interface NrrResponse {
  series: NrrPoint[];
  ttm_nrr_pct: number | null;
  ttm_gdr_pct: number | null;
  current_arr: number | null;
  cohort_month: string | null;
  cohort_starting_arr: number | null;
  cohort_ending_arr: number | null;
  cohort_arr_retained_pct: number | null;
  cohort_count: number | null;
  surviving_count: number | null;
}

export function useNrr() {
  return useQuery({
    queryKey: ["renewals", "dashboard", "nrr"],
    queryFn: () => apiFetch<NrrResponse>("/renewals/dashboard/nrr"),
  });
}

export interface RetentionResponse {
  customer_retention_pct: number | null;
  arr_retention_pct: number | null;
  accounts_in_window: number;
  arr_at_stake: number;
  retained_count: number;
  lost_count: number;
  retained_arr: number;
  lost_arr: number;
  by_month: Array<{ month: string; retained: number; lost: number }>;
  status_mix: Array<{ status: string; count: number }>;
}

export function useRetention() {
  return useQuery({
    queryKey: ["renewals", "dashboard", "retention"],
    queryFn: () => apiFetch<RetentionResponse>("/renewals/dashboard/retention"),
  });
}

export interface ForecastBucket {
  month: string;
  renew: number;
  at_risk: number;
  churn: number;
  unknown: number;
  renew_arr: number;
  at_risk_arr: number;
  churn_arr: number;
  unknown_arr: number;
}

export interface ForecastResponse {
  totals: {
    renew: number;
    at_risk: number;
    churn: number;
    unknown: number;
    renew_arr: number;
    at_risk_arr: number;
    churn_arr: number;
    unknown_arr: number;
    upcoming_count: number;
    coverage_pct: number | null;
  };
  by_month: ForecastBucket[];
  current_month: string;
}

export function useForecast() {
  return useQuery({
    queryKey: ["renewals", "dashboard", "forecast"],
    queryFn: () => apiFetch<ForecastResponse>("/renewals/dashboard/forecast"),
  });
}

export interface ForwardPoint {
  month: string;
  cohort_starting_arr: number;
  projected_ending_arr: number;
  expansion_arr_through: number;
  projected_churn_arr: number;
  projected_nrr_pct: number | null;
  projected_gdr_pct: number | null;
  cohort_count: number;
}

export interface ForwardResponse {
  latest_closed_period: string | null;
  current_month: string;
  horizon_month: string | null;
  forward_series: ForwardPoint[];
  projected_ttm_nrr_pct: number | null;
  projected_ttm_gdr_pct: number | null;
  arr_exposure_12mo: number;
  upcoming_count_12mo: number;
  coverage_pct: number | null;
  committed_expansion_12mo_arr: number;
}

export function useForward() {
  return useQuery({
    queryKey: ["renewals", "dashboard", "forward"],
    queryFn: () => apiFetch<ForwardResponse>("/renewals/dashboard/forward"),
  });
}

export interface CohortDetailRow {
  month: string;
  accts: number;
  arr: number;
  renew: number;
  at_risk: number;
  churn: number;
  unknown: number;
  renew_arr: number;
  at_risk_arr: number;
  churn_arr: number;
  unknown_arr: number;
  renewal_pct: number | null;
  renewal_arr_pct: number | null;
  at_risk_arr_pct: number | null;
  is_current_month: boolean;
  is_past: boolean;
  actuals_available: boolean;
  actuals_in_progress: boolean;
  actual_renew_arr: number | null;
  actual_renew_count: number | null;
  actual_churn_count: number | null;
  actual_cohort_pre_arr: number | null;
  actual_renewal_arr_pct: number | null;
  actual_contraction_arr: number | null;
  actual_contraction_pct: number | null;
  actual_expansion_arr: number | null;
  actual_expansion_pct: number | null;
}

export interface CohortDetailResponse {
  current_month: string;
  latest_closed_period: string | null;
  rows: CohortDetailRow[];
}

export function useCohortDetail() {
  return useQuery({
    queryKey: ["renewals", "dashboard", "cohort_detail"],
    queryFn: () => apiFetch<CohortDetailResponse>("/renewals/dashboard/cohort_detail"),
  });
}

// ─── data tab ─────────────────────────────────────────────────────

export interface OrphanResp<T> {
  rows: T[];
  count: number;
}

export function useOrphanContracts() {
  return useQuery({
    queryKey: ["renewals", "data", "orphan-contracts"],
    queryFn: () =>
      apiFetch<OrphanResp<Record<string, unknown>>>("/renewals/data/orphan-contracts"),
  });
}

export function useOrphanAccounts() {
  return useQuery({
    queryKey: ["renewals", "data", "orphan-accounts"],
    queryFn: () =>
      apiFetch<OrphanResp<Record<string, unknown>>>("/renewals/data/orphan-accounts"),
  });
}

export function useOrphanPosNoMaster() {
  return useQuery({
    queryKey: ["renewals", "data", "orphan-pos-no-master"],
    queryFn: () =>
      apiFetch<OrphanResp<Record<string, unknown>>>("/renewals/data/orphan-pos-no-master"),
  });
}

export function useOrphanPosNoCompany() {
  return useQuery({
    queryKey: ["renewals", "data", "orphan-pos-no-company"],
    queryFn: () =>
      apiFetch<OrphanResp<Record<string, unknown>>>("/renewals/data/orphan-pos-no-company"),
  });
}

// ─── account detail ──────────────────────────────────────────────

export interface AccountDetail {
  pipeline_row: PipelineRow | null;
  summary: Record<string, unknown> | null;
  contracts: Record<string, unknown>[];
  companies: Record<string, unknown>[];
  // `mrr` = subscription MRR; `usage` = non-subscription billed revenue that
  // month (ex pass-through). Optional so cached/older payloads still render.
  history: { period: string; mrr: number; usage?: number }[];
  annotation_log: Array<{
    id: string;
    field: string;
    /** Human-readable label for `field` (e.g. "First term start date"). Falls
     *  back to the raw field name when the backend has no mapping. */
    field_label?: string;
    old_value: string | null;
    new_value: string | null;
    changed_by_name: string | null;
    changed_at: string | null;
  }>;
  account_links: AccountDetailLink[];
}

export interface AccountDetailLink {
  id: string;
  source_qb_id: number;
  target_qb_id: number;
  relationship: "merged_into" | "split_from" | "subcustomer_of" | "continuation_of";
  effective_period: string | null;
  note: string | null;
  created_by_name: string | null;
  created_at: string | null;
  direction: "outgoing" | "incoming";
}

// ─── account-link CRUD + account search ──────────────────────────

export interface AccountSearchResult {
  qb_id: number;
  name: string;
}

export function useAccountSearch(q: string) {
  const trimmed = q.trim();
  return useQuery({
    queryKey: ["renewals", "account-search", trimmed],
    queryFn: () =>
      apiFetch<{ results: AccountSearchResult[] }>(
        `/renewals/accounts?q=${encodeURIComponent(trimmed)}&limit=15`,
      ),
    enabled: trimmed.length >= 2,
    staleTime: 30_000,
  });
}

export interface BillingOrphanResult {
  qb_id: number;
  name: string;
  last_sub_mrr: number;
  last_period: string | null;
}

/** Search counterparties that bill in QuickBooks but have no account record.
 *  Used as the "Billing-only QB ID" picker mode for sub-account linking.
 *  Passes q="" with preload=true to fetch the top N orphans for browsing. */
export function useBillingOrphanSearch(q: string, opts: { enabled?: boolean } = {}) {
  const trimmed = q.trim();
  const enabled = opts.enabled !== false;
  return useQuery({
    queryKey: ["renewals", "billing-orphans", trimmed],
    queryFn: () =>
      apiFetch<{ results: BillingOrphanResult[] }>(
        `/renewals/accounts/billing-orphans?q=${encodeURIComponent(trimmed)}&limit=25`,
      ),
    // Either we have a query (>=2 chars) or we're preloading (q empty).
    enabled: enabled && (trimmed.length === 0 || trimmed.length >= 2),
    staleTime: 30_000,
  });
}

export interface CreateLinkPayload {
  source_qb_id: number;
  target_qb_id: number;
  relationship: "merged_into" | "split_from" | "continuation_of" | "subcustomer_of";
  effective_period?: string | null;
  note?: string | null;
}

export function useCreateAccountLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateLinkPayload) =>
      apiFetch<AccountDetailLink>("/renewals/account-links", {
        method: "POST",
        body: payload,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["renewals", "account", vars.source_qb_id] });
      void qc.invalidateQueries({ queryKey: ["renewals", "account", vars.target_qb_id] });
      void qc.invalidateQueries({ queryKey: ["renewals", "pipeline"] });
    },
  });
}

export function useDeleteAccountLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) =>
      apiFetch<void>(`/renewals/account-links/${linkId}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["renewals", "account"] });
      void qc.invalidateQueries({ queryKey: ["renewals", "pipeline"] });
    },
  });
}

export function useAccountDetail(qbId: number) {
  return useQuery({
    queryKey: ["renewals", "account", qbId],
    queryFn: () => apiFetch<AccountDetail>(`/renewals/accounts/${qbId}`),
    enabled: !!qbId,
  });
}

// ─── SSE live refresh ─────────────────────────────────────────────
// Mount once per page that should react to cross-tab annotation edits.
// EventSource → debounce → invalidate. Keeps the dashboard + pipeline in
// sync without per-mutation manual invalidation across tabs.

const SSE_URL = (import.meta.env.VITE_API_BASE ?? "/api") + "/renewals/events";

export function useRenewalsLiveRefresh(enabled: boolean = true) {
  const qc = useQueryClient();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let es: EventSource | null;
    try {
      es = new EventSource(SSE_URL, { withCredentials: true });
    } catch {
      return;
    }
    const trigger = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["renewals"] });
      }, 800);
    };
    es.onmessage = trigger;
    es.addEventListener("renewals.annotation", trigger);
    es.onerror = () => {
      // EventSource auto-reconnects on transient failures; nothing to do here.
    };
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      es?.close();
    };
  }, [enabled, qc]);
}
